import React, { FC, useEffect } from 'react';

import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useFormik } from 'formik';

import {
  calendarFilter,
  currency,
  getWorkshiftsByDate,
  logos,
  pharmacistActions,
  PharmacistLegendVariant,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import {
  renderScreen,
  setBack,
  setDrawerContentPadding,
  setFooterShow,
  setTitle,
} from '../../actions/drawerActions';

import getLongTermContractLabels from '../common/LongTermContractLabels/LongTermContractLabels';
import DrawerOutOfDateTitle from '../common/DrawerOutOfDateTitle';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import CustomAvatarSection from '../common/CustomAvatarSection';
import {
  ChipType,
  Constants,
  ScreenTypes,
  toWeekdayWeekendFormat,
} from '../../helpers/Constants';
import useMultiShiftFilter from '../../hooks/useMultiShiftFilter';
import useSelfService from '../../hooks/useSelfService';
import CustomChip from '../common/CustomChip';
import CustomDropdown from '../common/CustomDropdown';

import WorkshiftListRightSection from './WorkshiftListRightSection';
import {
  pharmacistWorkshiftListBuilder,
  sortWorkshiftList,
  WorkshiftListSortOption,
} from './helper';
import strings from '../../localization';
import styles from './styles';

const { getWorkshiftsByDate: successAction } = pharmacistActions;
const { serviceWorkshiftDetails } = ScreenTypes;

interface IWorkshiftList {
  date: string;
  workshiftListSort?: WorkshiftListSortOption;
}

interface IHandleClick {
  workshiftId: string;
  startDate: string;
  pharmacyId: string;
  longTermShiftContractId: string;
  isAutoSyncRecord: boolean;
}

const sortOptions = (isSelfService: boolean) =>
  [
    ...(isSelfService
      ? [{ key: WorkshiftListSortOption.rating, label: strings.rating }]
      : []),
    { key: WorkshiftListSortOption.hourlyRate, label: strings.hourlyRate },
    { key: WorkshiftListSortOption.distance, label: strings.distance },
    { key: WorkshiftListSortOption.shiftType, label: strings.workshiftType },
  ];

const WorkshiftList: FC<IWorkshiftList> = ({
  date,
  workshiftListSort = WorkshiftListSortOption.shiftType,
}) => {
  const dispatch = useAppDispatch();

  const { pharmacistEvents: workshiftList } = useMultiShiftFilter();
  const allLogos = useAppSelector(logos);
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const filters = useAppSelector(calendarFilter);
  const { isSelfService } = useSelfService();
  const sortFormik = useFormik({
    initialValues: {
      workshiftListSort,
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const { weekendVolume, weekVolume } = filters ?? {};
  const isWeekendVolumeString = typeof weekendVolume === Constants.string;
  const isWeekVolumeString = typeof weekVolume === Constants.string;
  const selectedDate = date.split('T')[0];

  useEffect(() => {
    const title = (
      <DrawerOutOfDateTitle
        dateLabel={strings.availableWorkshiftFormatted}
        date={date}
      />
    );

    dispatch(setTitle({ title }));
    dispatch(setDrawerContentPadding('0px'));
    dispatch(setFooterShow(false));
    dispatch(setBack(null));
    dispatch(
      getWorkshiftsByDate({
        ...filters,
        weekendVolume: isWeekendVolumeString
          ? JSON.parse(weekendVolume)
          : weekendVolume,
        weekVolume: isWeekVolumeString ? JSON.parse(weekVolume) : weekVolume,
        selectedDate,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = pharmacistWorkshiftListBuilder(
    workshiftList,
    allLogos,
    timeFormat,
  );
  const sortedEvents = sortWorkshiftList(
    events,
    sortFormik.values.workshiftListSort as WorkshiftListSortOption | '',
  );

  const handleClick = ({
    workshiftId,
    startDate,
    pharmacyId,
    longTermShiftContractId,
    isAutoSyncRecord,
  }: IHandleClick) =>
    () => {
      dispatch(
        renderScreen({
          screenType: serviceWorkshiftDetails,
          screenNumber: 1,
          type: PharmacistLegendVariant.workshift,
          eventIds: [workshiftId],
          date: startDate,
          pharmacyId,
          extraParameters: {
            longTermShiftContractId,
            isAutoSyncIDMap: {
              [workshiftId]: isAutoSyncRecord,
            },
            workshiftListSort: sortFormik.values.workshiftListSort,
          },
        }),
      );
    };

  const totalShifts = sortedEvents.length;

  return (
    <>
      <Box sx={styles.header}>
        <Typography sx={styles.totalShifts}>
          {`${strings.totalWorkshifts}: (${totalShifts})`}
        </Typography>
        <CustomDropdown
          label={`${strings.sortBy}: `}
          horizontal
          fullWidth={false}
          placeholder={strings.select}
          errorContainer={false}
          customClass={styles.sortDropdown}
          formik={sortFormik}
          id="workshiftListSort"
          name="workshiftListSort"
          menuItems={sortOptions(isSelfService)}
        />
      </Box>

      {sortedEvents.map((item) => {
        const {
          config,
          title,
          avatar,
          workshiftId,
          pharmacyId,
          startDate,
          longTermShiftContractId,
          isAutoSyncRecord,
          contractEndDate,
          contractStartDate,
          weekDayHourlyRate,
          weekendHourlyRate,
          isInWatchlist,
          hourlyRate,
        } = item ?? {};

        const { ShiftLabel, RangeLabel } = getLongTermContractLabels({
          successAction,
          contractEndDate,
          contractStartDate,
          isLongTerm: !!longTermShiftContractId,
        });

        const rangeConfig = [
          { key: '5', value: <RangeLabel sx={styles.zeroMargin} /> },
        ];

        const label = longTermShiftContractId
          ? toWeekdayWeekendFormat(weekDayHourlyRate, weekendHourlyRate)
          : `${currency}${hourlyRate}${strings.perHour}`;

        return (
          <Box key={workshiftId} sx={styles.buttonContainer}>
            <CustomAvatarSection
              TopSection={<ShiftLabel sx={styles.shiftLabel} />}
              isAutoSyncRecord={isAutoSyncRecord}
              customMenuContainerStyle={styles.menu}
              successAction={successAction}
              config={
                longTermShiftContractId
                  ? [...rangeConfig, ...config.slice(2)]
                  : config
              }
              title={title}
              avatar={avatar}
              showFullImage={false}
              onClick={handleClick({
                workshiftId,
                startDate,
                pharmacyId,
                longTermShiftContractId: longTermShiftContractId ?? '',
                isAutoSyncRecord: !!isAutoSyncRecord,
              })}
              imageSuccessAction={successAction}
              menuActions={null}
              renderChip={
                longTermShiftContractId ? (
                  <CustomChip label={label} type={ChipType.hourlyRate} />
                ) : null
              }
              RightSection={(
                <WorkshiftListRightSection
                  isWatchlist={isInWatchlist}
                  hourlyRateLabel={label}
                />
              )}
              hourlyRate={hourlyRate}
            />
          </Box>
        );
      })}
    </>
  );
};

export default WorkshiftList;
